package info.rezident.app.data.quiz

import kotlinx.serialization.Serializable

@Serializable
data class RecommendationItem(
    val rank: Int,
    val name: String,
    val matchScore: Int, // Percentage match (e.g., 78, 72)
    val pros: List<String> = emptyList(),        // Strong matches from analysis (can be IDs or generated text)
    val cons: List<String> = emptyList(),        // Potential conflicts from analysis (can be IDs or generated text)
    // New fields: 4 categories from cloud function analysis
    val perfectAnswersHigh: List<String> = emptyList(),    // Both user and specialty scored high (4-5)
    val perfectAnswersLow: List<String> = emptyList(),     // Both user and specialty scored low (1-2)
    val differentAnswersHigh: List<String> = emptyList(),  // User high, specialty low
    val differentAnswersLow: List<String> = emptyList(),   // User low, specialty high
    // Legacy field - kept for CharacteristicTags UI component
    val characteristics: List<String> = emptyList()
) {
    /**
     * Get a random preview pro text based on answer categories
     * Priority: perfectAnswersHigh > perfectAnswersLow
     * @return Preview pro text or null if no categories available
     */
    fun getFirstPreviewPro(): String? {
        // Try perfectAnswersHigh first - randomly select one
        if (perfectAnswersHigh.isNotEmpty()) {
            val randomHighId = perfectAnswersHigh.random()
            val previewText = QuestionPreviewTextsRepository.getPreviewTextForCategory(
                randomHighId,
                "perfectAnswersHigh"
            )
            if (previewText != null) return previewText
        }

        // Try perfectAnswersLow second - randomly select one
        if (perfectAnswersLow.isNotEmpty()) {
            val randomLowId = perfectAnswersLow.random()
            val previewText = QuestionPreviewTextsRepository.getPreviewTextForCategory(
                randomLowId,
                "perfectAnswersLow"
            )
            if (previewText != null) return previewText
        }

        return null
    }

    /**
     * Get a random preview con text based on answer categories
     * Priority: differentAnswersLow > differentAnswersHigh
     * Note: Use remember() in the UI to cache the result for consistency across recompositions
     * @return Preview con text or null if no categories available
     */
    fun getFirstPreviewCon(): String? {
        // Try differentAnswersLow first (user low, specialty high - bigger mismatch) - randomly select one
        if (differentAnswersLow.isNotEmpty()) {
            val randomLowId = differentAnswersLow.random()
            val previewText = QuestionPreviewTextsRepository.getPreviewTextForCategory(
                randomLowId,
                "differentAnswersLow"
            )
            if (previewText != null) return previewText
        }

        // Try differentAnswersHigh second (user high, specialty low) - randomly select one
        if (differentAnswersHigh.isNotEmpty()) {
            val randomHighId = differentAnswersHigh.random()
            val previewText = QuestionPreviewTextsRepository.getPreviewTextForCategory(
                randomHighId,
                "differentAnswersHigh"
            )
            if (previewText != null) return previewText
        }

        return null
    }
}

@Serializable
data class RecommendationsResult(
    val recommendations: List<RecommendationItem>,
    val incompatibleRecommendations: List<RecommendationItem> = emptyList()
)